import React, { useEffect, useState, useMemo } from 'react'
import { Col, Form, Row, Tab, Tabs } from 'react-bootstrap'
import ApptToothModal from './ApptTooth.modal'
import { AppointmentFormData } from './Appointment.modal'
import AddCommonProcedureModal from './AddCommonProcedure.modal'
import {
  ICommonProcedure,
  IProcedureLogs,
  IToothDetails,
  Procedure,
} from '@dataTypes/appointment.type'
import {
  addCommonProcedure,
  getCommonProc,
  searchProcedure,
  deleteCommonProcedure,
  getBundledProcedures,
} from '@services/api/procedure.api'
import {
  InputField,
  Button,
  DataGridComponent,
  DataTreeGridComponent,
  ActionButtons,
} from '@components/common'
import moment from 'moment'
import { getTreatmentPlanByPatient } from '@services/api/treatment-plan.api'
import { ITreatmentPlan } from '@dataTypes/treatmentPlan.types'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import { v4 as uuid } from 'uuid'
import {
  createAppointmentProcedureRowId,
  expandProcedureToToothRows,
  findAppointmentProcedureRowIndex,
  getAppointmentProcedureRowKey,
  mergeAppointmentProcedureRows,
} from '@utils/appt-procedure.utils'
import {
  collectAppointmentToothSurfaceSiblingIndices,
  mergeAppointmentRowsToToothSurfaceEntries,
  toothDataToAppointmentLogEntries,
} from '@utils/tooth-surface-edit.utils'
import {
  resolveAppointmentProcedureClinicFee,
  resolveTxPlanItemClinicFee,
} from '@utils/appt-procedure-fee.utils'
import { getInsuranceFieldValues } from '@utils/insurance.util'
import {
  formatToothDetailsToothLabel,
  formatToothSurfaceEntriesForDisplay,
  normalizeSurfToAtomic,
  resolveProcedureSurfaceSelectionRules,
} from '@utils/procedure-surface-selection.utils'
import { logger } from '@utils/safeLogger'
type PlanProps = {
  formData: Partial<AppointmentFormData>
  handleChange: (
    field: string,
    value: string | number | Date | boolean | Partial<IProcedureLogs>[] | null,
  ) => void
}

type ToothSelectionEntry = {
  area_type?: number | null
  area?: string | null
  tooth?: string | null
  surf?: string | null
}

const groupToothDetailsAsInitialSelections = (
  entries: ToothSelectionEntry[] | undefined,
): IToothDetails[] => {
  if (!entries || entries.length === 0) {
    return []
  }

  const groupedByTooth = new Map<number, IToothDetails>()
  const toothDetails: IToothDetails[] = []

  entries.forEach((entry) => {
    const areaType = entry.area_type ?? 1

    if (areaType !== 1 || !entry.tooth) {
      if (entry.area) {
        toothDetails.push({ area_type: areaType, area: entry.area })
      }
      return
    }

    const toothNumber = Number(entry.tooth)
    if (Number.isNaN(toothNumber)) {
      return
    }

    const existingDetail = groupedByTooth.get(toothNumber)
    if (existingDetail) {
      if (entry.surf) {
        existingDetail.surf = normalizeSurfToAtomic([
          ...(existingDetail.surf ?? []),
          entry.surf,
        ])
      }
      return
    }

    const toothDetail: IToothDetails = {
      area_type: areaType,
      tooth_num: toothNumber,
    }
    if (entry.surf) {
      toothDetail.surf = normalizeSurfToAtomic([entry.surf])
    }

    groupedByTooth.set(toothNumber, toothDetail)
    toothDetails.push(toothDetail)
  })

  return toothDetails
}

const parseTpSurfaceToToothDetails = (surface: string): IToothDetails[] =>
  surface.split(', ').map((item: string) => {
    const parts = item.split('/')
    const tooth_num = Number(parts[0])
    const surf =
      parts.length > 1 ? normalizeSurfToAtomic([parts.slice(1).join('/')]) : []
    return {
      area_type: 1,
      tooth_num,
      surf,
    }
  })

// **Format Tooth Details Properly**
export const formatToothDetails = (
  toothDetails: IToothDetails[] | undefined,
): string => {
  if (!toothDetails || toothDetails.length === 0) return 'N/A'

  return toothDetails
    .map((detail) => formatToothDetailsToothLabel(detail))
    .filter(Boolean)
    .join(', ')
}

export const groupProceduresByCode = (
  procedures: Partial<IProcedureLogs>[] = [],
): Record<string, Partial<IProcedureLogs>[]> => {
  return procedures.reduce(
    (acc, proc) => {
      if (!proc.proc_code) return acc
      if (!acc[proc.proc_code]) acc[proc.proc_code] = []
      acc[proc.proc_code].push(proc)
      return acc
    },
    {} as Record<string, Partial<IProcedureLogs>[]>,
  )
}

const Plan: React.FC<PlanProps> = ({ formData, handleChange }) => {
  const [inputValue, setInputValue] = useState('')
  const [isToothModalOpen, setIsToothModalOpen] = useState(false)
  const [isCommonProcedureModalOpen, setIsCommonProcedureModalOpen] =
    useState(false)
  const [selectedProcedure, setSelectedProcedure] =
    useState<Partial<IProcedureLogs> | null>(null)
  const [editingSiblingRowKeys, setEditingSiblingRowKeys] = useState<string[]>(
    [],
  )
  const [procedureList, setProcedureList] = useState<Procedure[]>([])
  const [loading, setLoading] = useState(false)
  const [debouncedName, setDebouncedName] = useState('')
  const [commonProc, setCommonProc] = useState<ICommonProcedure[]>([])
  const [bundledProcedures, setBundledProcedures] = useState<any[]>([])
  const initialToothSelectionsForSelectedProcedure =
    useMemo((): IToothDetails[] => {
      if (!selectedProcedure || !isToothModalOpen) {
        return []
      }
      if (editingSiblingRowKeys.length > 0) {
        const procedures = formData.procedures ?? []
        const indices = editingSiblingRowKeys
          .map((rowKey) => findAppointmentProcedureRowIndex(procedures, rowKey))
          .filter((index) => index >= 0)
        const entries = mergeAppointmentRowsToToothSurfaceEntries(
          procedures,
          indices,
        )
        return groupToothDetailsAsInitialSelections(entries)
      }
      return groupToothDetailsAsInitialSelections(
        selectedProcedure.tooth_details,
      )
    }, [
      editingSiblingRowKeys,
      formData.procedures,
      isToothModalOpen,
      selectedProcedure,
    ])
  const selectedProcedureSurfaceSelection = useMemo(() => {
    if (!selectedProcedure) {
      return null
    }
    const matchedProcedure = procedureList.find(
      (procedure) => procedure.procedure_id === selectedProcedure.proc_id,
    )
    return resolveProcedureSurfaceSelectionRules(
      matchedProcedure?.procedure_dental_conditions,
      selectedProcedure.proc_description ?? null,
    )
  }, [procedureList, selectedProcedure])
  const [selectedTPId, setSelectedTPId] = useState<string | null>(null)
  const [treatPlanList, setTreatPlanList] = useState<ITreatmentPlan[]>([])
  const [searchProcedureFocused, setSearchProcedureFocused] = useState(false)
  const [streamlinedSteps, setStreamlinedSteps] = useState({
    showModal: false,
    editMode: false,
    editPosition: 0,
  })

  // Open tooth modal for a procedure
  const openToothModal = (proc: Partial<IProcedureLogs>): void => {
    setSelectedProcedure(proc)
    setIsToothModalOpen(true)
  }

  // Close tooth modal
  const closeToothModal = (): void => {
    setSelectedProcedure(null)
    setEditingSiblingRowKeys([])
    setIsToothModalOpen(false)
  }

  const saveToothSelection = (
    toothData: IToothDetails[],
    procData?: Partial<IProcedureLogs>,
  ): void => {
    const currentProcedure = procData ?? selectedProcedure
    if (!currentProcedure) return
    const editingKey =
      isToothModalOpen && selectedProcedure
        ? getAppointmentProcedureRowKey(selectedProcedure)
        : getAppointmentProcedureRowKey(currentProcedure)
    const primaryRowId =
      currentProcedure.id ?? (editingKey || createAppointmentProcedureRowId())
    const updatedRow: Partial<IProcedureLogs> = {
      ...currentProcedure,
      id: primaryRowId,
      tooth_details: toothDataToAppointmentLogEntries(toothData),
    }
    let procedures = (formData.procedures ?? []) as Partial<IProcedureLogs>[]
    if (editingSiblingRowKeys.length > 0) {
      procedures = procedures.filter((proc) => {
        const rowKey = getAppointmentProcedureRowKey(proc)
        return !editingSiblingRowKeys.includes(rowKey) || rowKey === editingKey
      })
    }
    const existingIndex = findAppointmentProcedureRowIndex(
      procedures,
      editingKey || String(primaryRowId),
    )
    if (existingIndex !== -1) {
      procedures[existingIndex] = updatedRow
    } else {
      procedures.push(updatedRow)
    }
    handleChange('procedures', procedures)
    if (isToothModalOpen) {
      closeToothModal()
    }
  }

  const skipToothSelectionNew = (procData?: Partial<IProcedureLogs>): void => {
    const currentProcedure = procData ?? selectedProcedure
    if (!currentProcedure) return
    const rowId = currentProcedure.id ?? createAppointmentProcedureRowId()
    const row: Partial<IProcedureLogs> = {
      ...currentProcedure,
      id: rowId,
      tooth_details: [],
    }
    const editingKey =
      isToothModalOpen && selectedProcedure
        ? getAppointmentProcedureRowKey(selectedProcedure)
        : ''
    const updatedProcedures = mergeAppointmentProcedureRows(
      formData.procedures,
      [row],
      editingKey || undefined,
    )
    handleChange('procedures', updatedProcedures)
    if (isToothModalOpen) {
      closeToothModal()
    }
  }

  // Open common procedure modal
  const openCommonProcedureModal = (): void => {
    setIsCommonProcedureModalOpen(true)
  }

  // Close common procedure modal
  const closeCommonProcedureModal = (): void => {
    setIsCommonProcedureModalOpen(false)
  }

  // Save common procedure from modal
  const saveCommonProcedure = async (procId: string): Promise<void> => {
    const response = await addCommonProcedure({
      procedure_id: procId,
    })

    if (response.statusCode === 201) {
      const newCommonProcedure = response.data?.commonProcedure
      if (newCommonProcedure) {
        setCommonProc((prev) => [...prev, newCommonProcedure.common_procedure])
      }
    }

    closeCommonProcedureModal()
  }

  const removeProcedureByRowKey = (rowKey: string): void => {
    if (!rowKey) return
    const target = formData.procedures?.find(
      (proc) => getAppointmentProcedureRowKey(proc) === rowKey,
    )
    let updatedProcedures = (formData.procedures ?? []).filter(
      (proc) => getAppointmentProcedureRowKey(proc) !== rowKey,
    )
    if (target?.parentId) {
      const parentId = target.parentId
      const remainingChildren = updatedProcedures.filter(
        (proc) => proc.parentId === parentId,
      )
      if (remainingChildren.length === 0) {
        updatedProcedures = updatedProcedures.filter(
          (proc) => proc.id !== parentId,
        )
      } else if (target.source === 2) {
        const parent = updatedProcedures.find((proc) => proc.id === parentId)
        if (parent) {
          const calculateSum = (field: keyof IProcedureLogs): number =>
            remainingChildren.reduce(
              (acc, curr) =>
                acc + Number(curr[field as keyof IProcedureLogs] ?? 0),
              0,
            )
          const parentIndex = updatedProcedures.findIndex(
            (proc) => proc.id === parentId,
          )
          if (parentIndex !== -1) {
            updatedProcedures[parentIndex] = {
              ...parent,
              clinic_fee: calculateSum('clinic_fee'),
              allowed_amount: calculateSum('allowed_amount'),
              write_off: calculateSum('write_off'),
              estimated_insurance_pay: calculateSum('estimated_insurance_pay'),
              estimated_patient_resp: calculateSum('estimated_patient_resp'),
            }
          }
        }
      }
    }
    handleChange('procedures', updatedProcedures)
  }

  // Remove all procedures with a given code (bundled procedure re-select)
  const removeAllProceduresByCode = (procCode: string): void => {
    let updatedProcedures: Partial<IProcedureLogs>[] = []

    const currentProcedure = formData.procedures?.find(
      (proc) => proc.proc_code === procCode,
    )
    let itemsCount = 0

    if (currentProcedure?.parentId) {
      itemsCount =
        formData.procedures?.filter(
          (proc) => proc.parentId === currentProcedure.parentId,
        ).length ?? 0

      updatedProcedures = (formData.procedures ?? []).filter((proc) => {
        return !(
          currentProcedure?.parentId &&
          itemsCount <= 1 &&
          proc.id === currentProcedure?.parentId
        )
      })
    } else if (currentProcedure?.source === 2) {
      updatedProcedures = (formData.procedures ?? []).filter((proc) => {
        return !(proc?.parentId === currentProcedure?.id)
      })
    } else {
      updatedProcedures = formData.procedures ?? []
    }

    updatedProcedures = (updatedProcedures ?? []).filter(
      (proc) => proc.proc_code !== procCode,
    )

    handleChange('procedures', updatedProcedures)
  }

  // Clear all procedures
  const clearProcedures = (): void => {
    handleChange('procedures', [])
    handleChange('tpId', null)
  }

  const editToothSelectionNew = (rowKey: string): void => {
    const index = findAppointmentProcedureRowIndex(formData.procedures, rowKey)
    if (index === -1) return
    const currentProcedure = formData.procedures?.[index]
    if (!currentProcedure) return
    const siblingIndices = collectAppointmentToothSurfaceSiblingIndices(
      formData.procedures ?? [],
      index,
    )
    const siblingKeys = siblingIndices
      .map((siblingIndex) =>
        getAppointmentProcedureRowKey(
          formData.procedures?.[siblingIndex] ?? {},
        ),
      )
      .filter((key) => key.length > 0)
    setEditingSiblingRowKeys(siblingKeys)
    setSelectedProcedure(currentProcedure)
    setIsToothModalOpen(true)
  }

  const skipToothSelectionMultiple = (
    procData?: any,
  ): Partial<IProcedureLogs>[] => {
    const currentProcedure = procData
    if (!currentProcedure) return [] as Partial<IProcedureLogs>[]

    const procedures: Partial<IProcedureLogs>[] = []

    return [...procedures, currentProcedure]
  }

  const procedureHandleChange = (value: string): void => {
    setInputValue(value)
    setDebouncedName(value) // Triggers API search
  }

  const handleProcedureSelect = (proc: Procedure): void => {
    const newProcedure: Partial<IProcedureLogs> = {
      procedure: proc,
      proc_id: proc.procedure_id,
      proc_code: proc.procedure_code,
      proc_description: proc.description,
      proc_date: new Date(),
      provider_id: formData.providerId ?? '',
      patient_id: formData.patientId ?? '',
      tooth: null,
      surf: null,
      priority: 0,
      proc_status: 0,
      source: 0,
      insurance_plan_id: formData.insuranceId || null,
      ...getInsuranceFieldValues(
        proc.procedure_id,
        proc?.procedure_settings?.clinic_fee,
        formData.insuranceId as string,
        formData.insuranceOptions as any[],
        formData.providerId || '',
      ),
      id: createAppointmentProcedureRowId(),
      parentId: null,
    }

    setInputValue('')
    setProcedureList([])

    //Open tooth modal (optional)
    openToothModal(newProcedure)
  }

  const handleCommonProcedureSelect = (data: ICommonProcedure): void => {
    const newProcedure: Partial<IProcedureLogs> = {
      procedure: data.procedure,
      proc_id: data.procedure_id,
      proc_code: data.procedure?.procedure_code ?? '',
      proc_description: data.procedure?.description ?? '',
      proc_date: moment().format('YYYY-MM-DD'),
      provider_id: formData.providerId ?? '',
      patient_id: formData.patientId ?? '',
      tooth: null,
      surf: null,
      priority: 0,
      proc_status: 0,
      source: 1,
      source_id: data.common_proc_id,
      insurance_plan_id: formData.insuranceId || null,
      ...getInsuranceFieldValues(
        data.procedure_id,
        data?.procedure?.procedure_settings?.clinic_fee,
        formData.insuranceId as string,
        formData.insuranceOptions as any[],
        formData.providerId || '',
      ),
      id: createAppointmentProcedureRowId(),
      parentId: null,
    }

    setSelectedProcedure(newProcedure)
    setIsToothModalOpen(true)
  }

  const handleTPProcedureSelect = (proc: any): void => {
    const isSelected = formData.procedures?.some(
      (p) =>
        p.proc_code === proc.procedure_code &&
        p.source === 3 &&
        p.source_id === proc.item_id,
    )
      ? true
      : false

    if (isSelected) {
      const updatedProcedures = (formData.procedures ?? []).filter(
        (p) => !(p.source === 3 && p.source_id === proc.item_id),
      )
      handleChange('procedures', updatedProcedures)
    } else {
      const newProcedure: Partial<IProcedureLogs> = {
        procedure: proc,
        proc_id: proc.procedure_id,
        proc_code: proc.procedure_code,
        proc_description: proc.description,
        proc_date: new Date(),
        provider_id: formData.providerId ?? '',
        patient_id: formData.patientId ?? '',
        tooth: null,
        surf: null,
        priority: 0,
        proc_status: 0,
        source: 3,
        source_id: proc.item_id,
        insurance_plan_id: formData.insuranceId || null,
        ...getInsuranceFieldValues(
          proc.procedure_id,
          resolveTxPlanItemClinicFee(proc),
          formData.insuranceId as string,
          formData.insuranceOptions as any[],
          formData.providerId || '',
        ),
        id: createAppointmentProcedureRowId(),
        parentId: null,
      }

      if (proc.surface) {
        saveToothSelection(
          parseTpSurfaceToToothDetails(proc.surface),
          newProcedure,
        )
      } else {
        skipToothSelectionNew(newProcedure)
      }
    }
  }

  const handleBundledProcedureSelect = (data: any): void => {
    let newlyAddedProcedures: Partial<IProcedureLogs>[] = []
    data.bundled_procedure_items.forEach((proc: any, index: number) => {
      const currentProcedures = formData.procedures ?? []
      const updatedProcedures = currentProcedures.filter(
        (x) => x.proc_code === proc.procedure.procedure_code,
      )

      if (
        updatedProcedures.length <= 0 &&
        newlyAddedProcedures.filter(
          (p) => p.proc_code === proc.procedure.procedure_code,
        ).length <= 0
      ) {
        removeAllProceduresByCode(proc.procedure.procedure_code)
      }

      if (index === 0) {
        const newProcedureBundle: Partial<IProcedureLogs> = {
          proc_id: data.bundled_procedure_id,
          proc_code: data.bundled_code,
          proc_description: data.bundled_name ?? '',
          proc_date: new Date(),
          provider_id: formData.providerId ?? '',
          patient_id: formData.patientId ?? '',
          tooth: null,
          surf: null,
          priority: 0,
          proc_status: 0,
          source: 2,
          id: data.bundled_procedure_id,
          parentId: null,
        }

        newlyAddedProcedures = [
          ...newlyAddedProcedures,
          ...skipToothSelectionMultiple(newProcedureBundle),
        ]
      }
      const newProcedure: Partial<IProcedureLogs> = {
        procedure: proc.procedure,
        proc_id: proc.procedure.procedure_id,
        proc_code: proc.procedure.procedure_code,
        proc_description: proc.procedure.description,
        proc_date: new Date(),
        provider_id: formData.providerId ?? '',
        patient_id: formData.patientId ?? '',
        tooth: null,
        surf: null,
        priority: 0,
        proc_status: 0,
        source: 2,
        source_id: proc.bundled_procedure_item_id,
        insurance_plan_id: formData.insuranceId || null,
        ...getInsuranceFieldValues(
          proc.procedure_id,
          proc?.procedure?.procedure_settings?.clinic_fee,
          formData.insuranceId as string,
          formData.insuranceOptions as any[],
          formData.providerId || '',
        ),
        id: proc.bundled_procedure_item_id,
        parentId: data.bundled_procedure_id,
      }

      newlyAddedProcedures = [
        ...newlyAddedProcedures,
        ...skipToothSelectionMultiple(newProcedure),
      ]
    })

    const bundledRecord = newlyAddedProcedures.find(
      (p) => p.id === data.bundled_procedure_id,
    )

    if (bundledRecord) {
      bundledRecord.clinic_fee = newlyAddedProcedures.reduce(
        (acc, curr) => acc + Number(curr.clinic_fee ?? 0),
        0,
      )
      bundledRecord.allowed_amount = newlyAddedProcedures.reduce(
        (acc, curr) => acc + Number(curr.allowed_amount ?? 0),
        0,
      )
      bundledRecord.write_off = newlyAddedProcedures.reduce(
        (acc, curr) => acc + Number(curr.write_off ?? 0),
        0,
      )
      bundledRecord.estimated_insurance_pay = newlyAddedProcedures.reduce(
        (acc, curr) => acc + Number(curr.estimated_insurance_pay ?? 0),
        0,
      )
      bundledRecord.estimated_patient_resp = newlyAddedProcedures.reduce(
        (acc, curr) => acc + Number(curr.estimated_patient_resp ?? 0),
        0,
      )
    }

    let updatedProcedures: Partial<IProcedureLogs>[] = formData.procedures ?? []

    newlyAddedProcedures.forEach((currentProcedure) => {
      const rowKey = getAppointmentProcedureRowKey(currentProcedure)
      const existingProcedureIndex = findAppointmentProcedureRowIndex(
        updatedProcedures,
        rowKey,
      )

      if (existingProcedureIndex !== -1) {
        updatedProcedures = updatedProcedures.map((proc, index) => {
          if (index === existingProcedureIndex) {
            return currentProcedure
          }
          return proc
        })
      } else {
        updatedProcedures = [...(updatedProcedures ?? []), currentProcedure]
      }
    })

    handleChange('procedures', updatedProcedures ?? [])
  }

  const handleTreatmentPlanChange = (tpId: string): void => {
    if (!tpId) return
    setSelectedTPId(tpId)
    handleChange('tpId', tpId)
  }

  const fetchProcedures = async (name: string): Promise<void> => {
    try {
      setLoading(true)
      const response = await searchProcedure(name, true) // Call the API
      if (response.statusCode === 200) {
        setProcedureList(response.data?.procedures)
      } else {
        setProcedureList([])
      }
    } catch (error) {
      logger.error('Error fetching procedures:', error)
      setProcedureList([])
    } finally {
      setLoading(false)
    }
  }

  const fetchCommonProc = async (): Promise<void> => {
    try {
      const response = await getCommonProc()
      if (response.statusCode === 200) {
        setCommonProc(response.data?.common_procedures)
      } else {
        setCommonProc([])
      }
    } catch (error) {
      logger.error('Error fetching common procedures:', error)
      setCommonProc([])
    } finally {
      setLoading(false)
    }
  }

  const fetchBundledProcedures = async (): Promise<void> => {
    try {
      const response = await getBundledProcedures()
      if (response.statusCode === 200) {
        setBundledProcedures(response.data?.bundled_procedures)
      } else {
        setBundledProcedures([])
      }
    } catch (error) {
      logger.error('Error fetching bundled procedures:', error)
      setBundledProcedures([])
    } finally {
      setLoading(false)
    }
  }

  const fetchTreatPlan = async (patientId: string): Promise<void> => {
    try {
      const response = await getTreatmentPlanByPatient(patientId)
      if (response.statusCode === 200) {
        setTreatPlanList(response.data?.treatment_plan ?? [])
      } else {
        setTreatPlanList([])
      }
    } catch (error) {
      logger.error('Error fetching treatment plan:', error)
      setTreatPlanList([])
    } finally {
      setLoading(false)
    }
  }

  const handleStreamlinedItemRemove = async (
    event: React.MouseEvent<HTMLSpanElement>,
    commonProcId: string,
  ): Promise<void> => {
    event.stopPropagation()
    try {
      const response = await deleteCommonProcedure(commonProcId)

      if (response.statusCode === 200) {
        setCommonProc((prev) =>
          prev.filter((item) => item.common_proc_id !== commonProcId),
        )
      }
    } catch (error) {
      logger.error('Error deleting common procedure:', error)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedName.trim()) {
        fetchProcedures(debouncedName.trim())
      } else {
        setProcedureList([])
      }
    }, 300) // Adjust debounce time as needed (e.g., 300ms)

    return () => clearTimeout(timer)
  }, [debouncedName])

  useEffect(() => {
    fetchCommonProc()
    fetchBundledProcedures()
    if (formData.patientId) {
      fetchTreatPlan(formData.patientId)
    }
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toothSurfaceTemplate = ({
    rowKey,
    noTS,
    hasNoAreaType,
    entries,
  }: {
    rowKey: string
    noTS: boolean
    hasNoAreaType: boolean
    entries: Partial<IProcedureLogs>[]
  }): JSX.Element => {
    const isVisible = !(noTS === undefined || noTS === true)
    const showEditPencil = isVisible && !hasNoAreaType && entries.length > 0

    return (
      <div className="tx-plan-fee-edit template-cell-tooth-surface-wrapper">
        <span className="template-cell-tooth-surface tx-plan-fee-edit-label">
          {!isVisible ? (
            <></>
          ) : hasNoAreaType ? (
            <Button
              size="xs"
              className="ps-0 pe-1 text-primary"
              onClick={() => editToothSelectionNew(rowKey)}
            >
              + Add T/S
            </Button>
          ) : (
            formatToothSurfaceEntriesForDisplay(
              entries.map((entry) => ({
                area_type: entry.area_type ?? null,
                area: entry.area ?? null,
                tooth: entry.tooth ?? null,
                surf: entry.surf ?? null,
              })),
            )
          )}
        </span>
        {showEditPencil && (
          <button
            type="button"
            aria-label="Edit tooth/surface"
            title="Edit tooth/surface"
            className="unstyled-button mgc_pencil_fill tx-plan-fee-edit-icon"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              editToothSelectionNew(rowKey)
            }}
          />
        )}
      </div>
    )
  }

  const columnsProcedures = useMemo(() => {
    return [
      {
        field: 'space',
        headerText: '',
        width: '16px',
      },
      {
        field: 'procCode',
        headerText: 'Code',
        width: '106px',
        footer: {
          template: () => <div>Total</div>,
        },
      },
      {
        field: 'description',
        headerText: 'Description',
        width: '160px',
        template: (props: any) => (
          <TooltipComponent
            content={props.description}
            opensOn="Hover"
            cssClass="e-tooltip-template-css e-tooltip-tab-feature"
            position="TopCenter"
          >
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '90%',
                display: 'block',
                fontSize: '0.875rem',
              }}
            >
              {props.description}
            </span>
          </TooltipComponent>
        ),
      },
      {
        field: 'toothsurface',
        headerText: 'T/S',
        width: '120px',
        template: toothSurfaceTemplate,
      },
      {
        field: 'clinic_fee',
        headerText: 'Clinic fee',
        width: '120px',
        template: (props: any) => {
          if (props.clinic_fee !== null && props.clinic_fee !== undefined) {
            return <span>${Number(props.clinic_fee).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'allowed_amount',
        headerText: 'Allowed amt.',
        width: '120px',
        template: (props: any) => {
          if (
            props.allowed_amount !== null &&
            props.allowed_amount !== undefined
          ) {
            return <span>${Number(props.allowed_amount).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'write_off',
        headerText: 'Write-off',
        width: '120px',
        template: (props: any) => {
          if (props.write_off !== null && props.write_off !== undefined) {
            return <span>${Number(props.write_off).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'estimated_insurance_pay',
        headerText: 'Est. ins. pay',
        width: '120px',
        template: (props: any) => {
          if (
            props.estimated_insurance_pay !== null &&
            props.estimated_insurance_pay !== undefined
          ) {
            return (
              <span>${Number(props.estimated_insurance_pay).toFixed(2)}</span>
            )
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'estimated_patient_resp',
        headerText: 'Est. pt. resp.',
        width: 'auto',
        template: (props: any) => {
          if (
            props.estimated_patient_resp !== null &&
            props.estimated_patient_resp !== undefined
          ) {
            return (
              <span>${Number(props.estimated_patient_resp).toFixed(2)}</span>
            )
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        headerText: 'Action',
        field: '',
        width: '100px',
        textAlign: 'Center' as const,
        template: (props: { rowKey: string }) => (
          <ActionButtons
            onDelete={() => removeProcedureByRowKey(props.rowKey)}
          />
        ),
      },
    ].map((column) => ({
      ...column,
    }))
  }, [toothSurfaceTemplate, formData.procedures])

  const columnsTPProcedures = useMemo(() => {
    return [
      {
        field: 'space',
        headerText: '',
        width: '46px',
        template: (props: any) => {
          return (
            <div className="d-flex justify-content-center">
              <Form.Check
                type="checkbox"
                className="d-flex"
                onChange={() => handleTPProcedureSelect(props)}
                checked={
                  formData.procedures?.some(
                    (p) =>
                      p.proc_code === props.procedure_code &&
                      p.source === 3 &&
                      p.source_id === props.item_id,
                  )
                    ? true
                    : false
                }
              />
            </div>
          )
        },
      },
      {
        field: 'procedure_code',
        headerText: 'Code',
        width: '106px',
        footer: {
          template: () => <div>Total</div>,
        },
      },
      {
        field: 'description',
        headerText: 'Description',
        width: '160px',
        template: (props: any) => (
          <TooltipComponent
            content={props.description}
            opensOn="Hover"
            cssClass="e-tooltip-template-css e-tooltip-tab-feature"
            position="TopCenter"
          >
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '90%',
                display: 'block',
              }}
            >
              {props.description}
            </span>
          </TooltipComponent>
        ),
      },
      {
        field: 'surface',
        headerText: 'T/S',
        width: '120px',
        template: (props: any) => {
          if (props.surface) {
            return <span>{props.surface}</span>
          }
          return <></>
        },
      },
      {
        field: 'phase_name',
        headerText: 'Phase',
        width: 'auto',
      },
    ].map((column) => ({
      ...column,
    }))
  }, [treatPlanList, formData.procedures])

  const procedureDataArrayNew =
    formData.procedures?.map((procedure, index) => {
      const noTS = procedure.source === 2 && procedure.parentId === null
      const typedEntries =
        procedure.tooth_details ?? ([] as Partial<IProcedureLogs>[])
      const hasNoAreaType = typedEntries.every(
        (e) => e.area_type === null || e.area_type === undefined,
      )
      const rowId =
        procedure.proc_log_id ?? procedure.id ?? `appt-proc-row-${index}`
      const rowKey = getAppointmentProcedureRowKey({
        ...procedure,
        id: rowId,
      })

      return {
        ...procedure,
        id: rowId,
        rowKey: rowKey || rowId,
        procCode: procedure.proc_code,
        description: procedure.proc_description,
        noTS,
        hasNoAreaType,
        entries: typedEntries,
        ...(!procedure.parentId ? { parentId: null } : {}),
      }
    }) ?? []

  const getNumberOfSelectedProcedures = useMemo(() => {
    return (source: number): string => {
      const count =
        formData.procedures?.filter((p) => {
          return (
            Number(p.source) === source ||
            (source === 1 && p.source === 2 && p.parentId != null)
          )
        }).length ?? 0

      return count > 0 ? `${count} selected` : ''
    }
  }, [formData.procedures])

  const treatmentPlanProcedures = (): any[] => {
    return treatPlanList.flatMap(
      (tp) =>
        tp.phases?.flatMap((p) =>
          p.items?.map((item) => ({
            ...item,
            phase_name: p.phase_name,
            tp_id: tp.tp_id,
            procedure_code: item?.procedure_settings?.procedure?.procedure_code,
            description: item?.procedure_settings?.procedure?.description,
          })),
        ) ?? [],
    )
  }

  const addNewMultipleTPProcedures = (): void => {
    const newlyAddedProcedures: Partial<IProcedureLogs>[] = []
    treatmentPlanProcedures()
      .filter((p) => p.tp_id === formData.tpId)
      .forEach((proc) => {
        const alreadyAdded = (formData.procedures ?? []).some(
          (p) => p.source === 3 && p.source_id === proc.item_id,
        )
        if (alreadyAdded) {
          return
        }

        const newProcedure: Partial<IProcedureLogs> = {
          procedure: proc,
          proc_id: proc.procedure_id,
          proc_code: proc.procedure_code,
          proc_description: proc.description,
          proc_date: new Date(),
          provider_id: formData.providerId ?? '',
          patient_id: formData.patientId ?? '',
          tooth: null,
          surf: null,
          priority: 0,
          proc_status: 0,
          source: 3,
          source_id: proc.item_id,
          insurance_plan_id: formData.insuranceId || null,
          ...getInsuranceFieldValues(
            proc.procedure_id,
            resolveTxPlanItemClinicFee(proc),
            formData.insuranceId as string,
            formData.insuranceOptions as any[],
            formData.providerId || '',
          ),
          id: createAppointmentProcedureRowId(),
          parentId: null,
        }

        if (proc.surface) {
          newlyAddedProcedures.push(
            ...expandProcedureToToothRows(
              newProcedure,
              parseTpSurfaceToToothDetails(proc.surface),
            ),
          )
        } else {
          newlyAddedProcedures.push({ ...newProcedure, tooth_details: [] })
        }
      })

    handleChange('procedures', [
      ...(formData.procedures ?? []),
      ...newlyAddedProcedures,
    ])
  }

  const removeMultipleTPProcedures = (): void => {
    const tpItemIds = new Set(
      treatmentPlanProcedures()
        .filter((p) => p.tp_id === formData.tpId)
        .map((proc) => proc.item_id),
    )
    const currentProcedures = (formData.procedures ?? []).filter(
      (p) => !(p.source === 3 && p.source_id && tpItemIds.has(p.source_id)),
    )
    handleChange('procedures', currentProcedures)
  }

  useEffect(() => {
    const tpItems = treatmentPlanProcedures().filter(
      (p) => p.tp_id === formData.tpId,
    )
    let selected = tpItems.length > 0
    tpItems.forEach((item) => {
      if (
        !formData.procedures?.some(
          (p) => p.source === 3 && p.source_id === item.item_id,
        )
      ) {
        selected = false
      }
    })

    const treatmentPlanSelectAll = document.querySelector(
      'input[id="treatment-plan-select-all"]',
    )
    if (treatmentPlanSelectAll) {
      ;(treatmentPlanSelectAll as HTMLInputElement).checked = selected
    }
  }, [selectedTPId, formData.procedures])

  const updateSelectedProcedures = (): void => {
    let updatedProcedures: any[] = formData.procedures ?? []
    updatedProcedures = updatedProcedures.map((p) => {
      if (
        p.source !== 2 ||
        (p.source === 2 && p.parentId !== null && p.parentId !== undefined)
      ) {
        return {
          ...p,
          insurance_plan_id: formData.insuranceId || null,
          ...getInsuranceFieldValues(
            p.proc_id,
            resolveAppointmentProcedureClinicFee(p),
            formData.insuranceId as string,
            formData.insuranceOptions as any[],
            formData.providerId || '',
          ),
        }
      }

      return p
    })

    updatedProcedures = updatedProcedures.map((p) => {
      if (p.source === 2 && (p.parentId === null || p.parentId === undefined)) {
        const childProcedures = updatedProcedures.filter(
          (x) => x.parentId === p.id,
        )

        const calculateSum = (field: keyof IProcedureLogs): number =>
          childProcedures.reduce(
            (acc, curr) =>
              acc + Number(curr[field as keyof IProcedureLogs] ?? 0),
            0,
          )

        return {
          ...p,
          clinic_fee: calculateSum('clinic_fee'),
          allowed_amount: calculateSum('allowed_amount'),
          write_off: calculateSum('write_off'),
          estimated_insurance_pay: calculateSum('estimated_insurance_pay'),
          estimated_patient_resp: calculateSum('estimated_patient_resp'),
        }
      }

      return p
    })

    handleChange('procedures', updatedProcedures)
  }

  useEffect(() => {
    if ((formData.procedures ?? []).length > 0) {
      const insurancePlanIdSource = (formData.procedures ?? [])[0]
        .insurance_plan_id
      const providerIdSource = (formData.procedures ?? [])[0].provider_id
      if (
        insurancePlanIdSource !== (formData.insuranceId || null) ||
        providerIdSource !== (formData.providerId || null)
      ) {
        setTimeout(() => {
          updateSelectedProcedures()
        }, 500)
      }
    }
  }, [formData.insuranceId, formData.providerId])

  return (
    <>
      <div className="mt-2 appt-modal">
        <Row>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Select procedure(s)</Form.Label>
            </Form.Group>
            <div className="accordion" id="accordionSelectProcedure">
              <div className="accordion-item" key={'procedure-codes'}>
                <h2 className="ac-header appt-accordion">
                  <button
                    type="button"
                    className="unstyled-button accordion-button collapsed"
                    data-bs-toggle="collapse"
                    data-bs-target={`#procedure-codes`}
                    aria-expanded="false"
                    aria-controls={'procedure-codes'}
                  >
                    Procedure codes
                    <span className="selected-count">
                      {getNumberOfSelectedProcedures(0)}
                    </span>
                  </button>
                </h2>
                <div
                  id="procedure-codes"
                  className="accordion-collapse collapse"
                  data-bs-parent="#accordionSelectProcedure"
                >
                  <div className="accordion-body">
                    <Form.Group>
                      <Form.Label>Procedures</Form.Label>
                      <div
                        className={`tag-input-container search-procedure-container ${searchProcedureFocused ? 'focused' : ''}`}
                      >
                        {formData.procedures
                          ?.filter((p) => p.source === 0)
                          .map((procedure) => {
                            const rowKey =
                              getAppointmentProcedureRowKey(procedure)
                            return (
                              <TooltipComponent
                                key={rowKey || uuid()}
                                content={() => {
                                  const entries = procedure.tooth_details ?? []
                                  return (
                                    <div style={{ textAlign: 'left' }}>
                                      {procedure.proc_description}
                                      <br />
                                      {formatToothSurfaceEntriesForDisplay(
                                        (entries || []).map((entry) => ({
                                          area_type: entry.area_type ?? null,
                                          area: entry.area ?? null,
                                          tooth: entry.tooth ?? null,
                                          surf: entry.surf ?? null,
                                        })),
                                      )}
                                    </div>
                                  )
                                }}
                                opensOn="Hover"
                                cssClass="e-tooltip-template-css e-tooltip-tab-feature e-tooltip-procedure"
                                position="BottomCenter"
                              >
                                <div className="tag-item">
                                  {procedure.proc_code}
                                  <button
                                    type="button"
                                    className="unstyled-button tag-item-close ti ti-x"
                                    aria-label="Remove procedure"
                                    onClick={() =>
                                      removeProcedureByRowKey(rowKey)
                                    }
                                  />
                                </div>
                              </TooltipComponent>
                            )
                          })}

                        <InputField
                          value={inputValue}
                          placeholder={
                            formData?.procedures?.length || 0 > 0
                              ? ''
                              : 'Add procedure'
                          }
                          onChange={(e) =>
                            procedureHandleChange(e.target.value)
                          }
                          onFocus={() => setSearchProcedureFocused(true)}
                          onBlur={() => setSearchProcedureFocused(false)}
                          className="tag-input flex-grow-1 border-0 shadow-none w-auto"
                        />

                        {/* Clear button */}
                        {(formData.procedures?.length ?? 0) > 0 && (
                          <Button
                            className="button-link text-primary ms-2 clear-procedures-button"
                            onClick={clearProcedures}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </Form.Group>

                    {/* Show dropdown only when there are results */}
                    {procedureList.length > 0 && (
                      <div className="procedure-dropdown">
                        <ul className="dropdown-list">
                          {procedureList.map((proc) => (
                            <li
                              key={proc.procedure_id}
                              onClick={() => handleProcedureSelect(proc)}
                              className="dropdown-item proc-dropdown-item"
                            >
                              {proc.procedure_code} - {proc.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="accordion-item" key={'favourites-bundles'}>
                <h2 className="ac-header appt-accordion">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#favourites-bundles`}
                    aria-expanded="false"
                    aria-controls={'favourites-bundles'}
                  >
                    Favourites & bundles
                    <span className="selected-count">
                      {getNumberOfSelectedProcedures(1)}
                    </span>
                  </button>
                </h2>
                <div
                  id="favourites-bundles"
                  className="accordion-collapse collapse"
                  data-bs-parent="#accordionSelectProcedure"
                >
                  <div className="accordion-body">
                    <Tabs>
                      <Tab eventKey="favourites" title={<>Favourites</>}>
                        <div className="dental-grid mt-2">
                          {commonProc.length > 0 ? (
                            commonProc.map((item: any) => {
                              return (
                                <button
                                  type="button"
                                  key={item.common_proc_id}
                                  className="unstyled-button dental-card"
                                  onClick={() =>
                                    handleCommonProcedureSelect(item)
                                  }
                                >
                                  <span className="dental-description ">
                                    <TooltipComponent
                                      content={
                                        item.procedure?.description ?? ''
                                      }
                                      opensOn="Hover"
                                      cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                                      position="TopCenter"
                                    >
                                      <span>
                                        {item.procedure?.description ?? ''}
                                      </span>
                                    </TooltipComponent>
                                  </span>
                                  <span className="dental-details">
                                    {item.procedure?.procedure_code ?? ''}
                                  </span>
                                </button>
                              )
                            })
                          ) : (
                            <span className="text-optional ms-2">
                              No favourites to display.
                            </span>
                          )}
                        </div>
                      </Tab>
                      <Tab eventKey="bundles" title={<>Bundles</>}>
                        <div className="dental-grid mt-2">
                          {bundledProcedures.length > 0 ? (
                            bundledProcedures.map((item: any) => {
                              return (
                                <button
                                  type="button"
                                  key={item.bundled_procedure_id}
                                  className="unstyled-button dental-card"
                                  onClick={() =>
                                    handleBundledProcedureSelect(item)
                                  }
                                >
                                  <span className="dental-description ">
                                    <TooltipComponent
                                      content={`${item.bundled_code} - ${item.bundled_name}`}
                                      opensOn="Hover"
                                      cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                                      position="TopCenter"
                                    >
                                      {item.bundled_code}
                                      <span className="text-optional">{` (${item.bundled_name})`}</span>
                                    </TooltipComponent>
                                  </span>
                                  <span className="dental-details">
                                    {item.bundled_procedure_items
                                      .map(
                                        (p: any) => p.procedure.procedure_code,
                                      )
                                      .join(', ')}
                                  </span>
                                </button>
                              )
                            })
                          ) : (
                            <span className="text-optional ms-2">
                              No bundles to display.
                            </span>
                          )}
                        </div>
                      </Tab>
                    </Tabs>
                  </div>
                </div>
              </div>
              <div className="accordion-item" key={'treatment-plan'}>
                <h2 className="ac-header appt-accordion">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#treatment-plan`}
                    aria-expanded="false"
                    aria-controls={'treatment-plan'}
                  >
                    Treatment plan
                    <span className="selected-count">
                      {getNumberOfSelectedProcedures(3)}
                    </span>
                  </button>
                </h2>
                <div
                  id="treatment-plan"
                  className="accordion-collapse collapse"
                  data-bs-parent="#accordionSelectProcedure"
                >
                  <div className="accordion-body pb-0">
                    <Form.Group>
                      <Form.Label>Select treatment plan (optional)</Form.Label>
                      <Form.Select
                        value={formData.tpId ?? ''}
                        onChange={(e) =>
                          handleTreatmentPlanChange(e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        {treatPlanList.map((plan) => (
                          <option key={plan.tp_id} value={plan.tp_id}>
                            {plan.tp_heading}
                          </option>
                        ))}
                      </Form.Select>
                      <Col
                        md={12}
                        className="grid-procedures-container treatment-plan-grid mt-2"
                      >
                        <div className="select-all-checkbox">
                          <Form.Check
                            type="checkbox"
                            className="d-flex"
                            id="treatment-plan-select-all"
                            onChange={(e) => {
                              if (e.target.checked) {
                                addNewMultipleTPProcedures()
                              } else {
                                removeMultipleTPProcedures()
                              }
                            }}
                          />
                        </div>
                        <DataGridComponent
                          data={treatmentPlanProcedures().filter(
                            (p) => p.tp_id === formData.tpId,
                          )}
                          columns={columnsTPProcedures}
                          rowHeight={36}
                          allowTextWrap={true}
                        />
                      </Col>
                    </Form.Group>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
        {(formData.procedures?.length ?? 0) > 0 && (
          <Row
            key={`${formData.procedures?.length ?? 0}${formData.procedures?.flatMap((p) => p.tooth_details ?? []).length ?? 0}`}
          >
            <Col
              md={12}
              className="grid-procedures-container selected-procedures-grid"
            >
              <Form.Group>
                <div
                  style={{
                    marginBottom: '8px',
                    border: 0,
                    height: '1px',
                    background: 'var(--gray-200)',
                  }}
                ></div>
                <Form.Label>Procedures selected</Form.Label>
              </Form.Group>
              <DataTreeGridComponent
                data={procedureDataArrayNew}
                columns={columnsProcedures}
                idMapping="id"
                parentIdMapping="parentId"
                treeColumnIndex={1}
                rowHeight={36}
                allowPaging={true}
                allowTextWrap={true}
                pageSize={10}
                hasFooter={true}
                footerData={columnsProcedures
                  .filter((col) => col.footer)
                  .map((col) => ({
                    field: col.field,
                    template: col.footer?.template ?? (() => <></>),
                  }))}
              />
            </Col>
          </Row>
        )}
      </div>

      {/* Tooth Selection Modal */}
      <ApptToothModal
        isOpen={isToothModalOpen}
        saveToothSelection={saveToothSelection}
        closeModal={closeToothModal}
        skipToothSelection={skipToothSelectionNew}
        initialToothSelections={initialToothSelectionsForSelectedProcedure}
        procedureSurfaceSelection={selectedProcedureSurfaceSelection}
        procedureSurfaceFilterText={selectedProcedure?.proc_description ?? null}
      />
      <AddCommonProcedureModal
        key={isCommonProcedureModalOpen ? 'open' : 'closed'}
        isOpen={isCommonProcedureModalOpen}
        closeModal={closeCommonProcedureModal}
        commonProcedures={commonProc}
        saveCommonProcedure={saveCommonProcedure}
      />
    </>
  )
}

export default Plan
